import { useMemo } from 'react';
import './dtm.css';
import JsonLd from '../../components/feature/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://dtm.now';

const HomePage = () => {
  const jsonLdData = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'DTM.now',
        url: siteUrl,
        description:
          'DTM.now connects authoritative data sources directly to AI agents — federated, metered per query, and cryptographically signed.',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'DTM.now',
        url: siteUrl,
        description:
          'Ground truth infrastructure for AI — a federated, metered, cryptographically signed verified data layer connecting authoritative sources to AI agents.',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'info@dtm.now',
          contactType: 'customer support',
        },
        sameAs: [],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${siteUrl}/`,
        url: `${siteUrl}/`,
        name: 'DTM.now — Ground Truth Infrastructure for AI',
        description:
          'DTM.now connects authoritative data sources directly to AI agents — federated, metered per query, and cryptographically signed. The verified data layer for AI.',
        isPartOf: { '@id': siteUrl },
        dateModified: '2026-03-21',
        inLanguage: 'en',
        mainEntity: {
          '@type': 'SoftwareApplication',
          name: 'DTM.now',
          applicationCategory: 'DeveloperApplication',
          operatingSystem: 'Web',
          description:
            'A federated verified data network that connects primary data sources to AI agents with per-query metering and cryptographic attestation.',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
            description: 'Pay-per-query pricing — no subscription required.',
          },
          featureList: [
            'Federated architecture — data stays with its owner',
            'Per-query metering and automatic revenue share',
            'Cryptographically signed responses with timestamp',
            'Native MCP server for any agent framework',
            'x402 support for fully autonomous agents',
            'REST API with structured query objects',
          ],
        },
      },
    ],
    [],
  );

  return (
    <div className="page">
      <JsonLd data={jsonLdData} />

      <div className="topbar">
        <span className="wordmark">dtm.now</span>
        <span className="topbar-right">Private beta — ground truth infrastructure</span>
      </div>

      <p className="eyebrow">The verified data layer for AI</p>

      <h1 className="hero-headline">
        Real answers require<br />
        <em>real sources.</em>
      </h1>

      <p className="hero-sub">
        DTM.now connects authoritative data sources directly to AI agents — federated, metered per query, and cryptographically signed. The data stays with its owner. The value flows both ways.
      </p>

      <div className="hero-tags">
        <span className="tag">Federated architecture</span>
        <span className="tag">Per-query metering</span>
        <span className="tag">Cryptographic attestation</span>
        <span className="tag">x402 native</span>
        <span className="tag">MCP compatible</span>
      </div>

      <hr className="rule" />

      <p className="section-label">Two sides. One network.</p>

      <div className="two-col">

        <div className="audience-card sot">
          <span className="card-pill sot">Source of truth</span>
          <h2 className="card-headline">Your verified data earns every time an AI queries it.</h2>
          <p className="card-body">
            AI agents use your data constantly. With DTM.now, that usage becomes a revenue stream — automated, auditable, and entirely on your terms. Your data never leaves your system.
          </p>
          <ul className="feat-list">
            <li><span className="dot-sot">·</span> Federated — your data stays in your infrastructure</li>
            <li><span className="dot-sot">·</span> Automatic revenue share on every query</li>
            <li><span className="dot-sot">·</span> Full audit trail of who queried what and when</li>
            <li><span className="dot-sot">·</span> Revoke or adjust access at any time</li>
            <li><span className="dot-sot">·</span> Works with your existing query interface</li>
          </ul>
          <a href="#interest-form" className="cta-btn sot">I have verified data →</a>
        </div>

        <div className="audience-card model">
          <span className="card-pill model">AI platform / agent</span>
          <h2 className="card-headline">Stop guessing on data that changes every day.</h2>
          <p className="card-body">
            Licenses lapse. Laws change. Medical evidence evolves. Court records update. DTM.now gives your agents live, signed, primary-source answers — not a six-month-old inference.
          </p>
          <ul className="feat-list">
            <li><span className="dot-model">·</span> Cryptographically signed responses with timestamp</li>
            <li><span className="dot-model">·</span> Pay per query — no subscription or account needed</li>
            <li><span className="dot-model">·</span> Native MCP server for any agent framework</li>
            <li><span className="dot-model">·</span> x402 support for fully autonomous agents</li>
            <li><span className="dot-model">·</span> REST API with structured query objects</li>
          </ul>
          <a href="#interest-form" className="cta-btn model">I need verified data →</a>
        </div>

      </div>

      <hr className="rule" />

      <p className="section-label">How value flows</p>

      <div className="steps">
        <div className="step">
          <p className="step-n">01 — connect</p>
          <p className="step-title">SOT registers</p>
          <p className="step-body">A registry, board, or expert publisher connects to DTM.now. Their data stays in their system. We route authenticated queries to them.</p>
        </div>
        <div className="step">
          <p className="step-n">02 — query</p>
          <p className="step-title">Agent asks</p>
          <p className="step-body">An AI agent submits a structured query. DTM.now routes it, normalizes the response, and returns a cryptographically signed answer with a freshness timestamp.</p>
        </div>
        <div className="step">
          <p className="step-n">03 — settle</p>
          <p className="step-title">Value distributes</p>
          <p className="step-body">The agent pays per query. DTM.now takes a platform fee and automatically distributes the remainder to the source. No invoicing. No manual reconciliation.</p>
        </div>
      </div>

      <hr className="rule" />

      <p className="section-label">Data in the network</p>

      <div className="data-grid">
        <div className="data-cell">
          <p className="data-type">Professional credentials</p>
          <p className="data-eg">Pilot certificates, contractor licenses, financial advisor registration</p>
        </div>
        <div className="data-cell">
          <p className="data-type">Medical research</p>
          <p className="data-eg">Peer-reviewed studies, clinical evidence, treatment guidelines</p>
        </div>
        <div className="data-cell">
          <p className="data-type">Court records</p>
          <p className="data-eg">Federal and state filings, rulings, active case status</p>
        </div>
        <div className="data-cell">
          <p className="data-type">Legislative data</p>
          <p className="data-eg">Current law, bill status, regulatory changes</p>
        </div>
        <div className="data-cell">
          <p className="data-type">Corporate filings</p>
          <p className="data-eg">SEC disclosures, registration status, officer records</p>
        </div>
        <div className="data-cell">
          <p className="data-type">Expert analysis</p>
          <p className="data-eg">Verified creator content with established subject-matter authority</p>
        </div>
      </div>

      <hr className="rule" />

      <p className="section-label">For independent creators and publishers</p>

      <div className="creator-block">
        <div>
          <h3 className="creator-headline">Your expertise has been powering AI answers. Start getting paid for it.</h3>
          <p className="creator-body">
            Independent writers, researchers, and analysts who publish verified, expert-level content are sitting on a new kind of asset. Every time an AI agent needs a credible answer in your domain, that&apos;s a query DTM.now can route to you — and compensate you for automatically.
          </p>
          <p className="creator-body">
            You set the terms. You keep the rights. You earn per query, not per pageview.
          </p>
          <div className="creator-platforms">
            <span className="platform-tag">Substack</span>
            <span className="platform-tag">Quora</span>
            <span className="platform-tag">Independent research</span>
            <span className="platform-tag">Newsletters</span>
            <span className="platform-tag">Academic blogs</span>
          </div>
        </div>
        <div className="creator-right">
          <div className="creator-stat">
            <p className="creator-stat-num">Per query</p>
            <p className="creator-stat-label">Revenue model — not per pageview, not per subscriber. Every AI query to your content earns.</p>
          </div>
          <div className="creator-stat">
            <p className="creator-stat-num">Your terms</p>
            <p className="creator-stat-label">You set the price, the access rules, and can revoke at any time. Full control.</p>
          </div>
          <div className="creator-stat">
            <p className="creator-stat-num">Zero transfer</p>
            <p className="creator-stat-label">Your content stays where it is. DTM.now routes queries to you. No custody of your work.</p>
          </div>
        </div>
      </div>

      <hr className="rule" />

      <p className="section-label">How we protect your data</p>

      <div className="data-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
        <div className="data-cell">
          <p className="data-type">SOT identity concealed</p>
          <p className="data-eg">Agents never see which source sits behind a query. Your endpoint, credentials, and query format are never exposed to the demand side.</p>
        </div>
        <div className="data-cell">
          <p className="data-type">Cryptographic binding</p>
          <p className="data-eg">Every response is signed and bound to the specific requester and timestamp. Responses cannot be reused, reshared, or replayed by any other party.</p>
        </div>
        <div className="data-cell">
          <p className="data-type">Watermarked responses</p>
          <p className="data-eg">Every query carries an imperceptible watermark tied to the requesting account. If your data appears somewhere it shouldn&apos;t, we can trace it.</p>
        </div>
        <div className="data-cell">
          <p className="data-type">Anomaly detection</p>
          <p className="data-eg">Behavioral analysis flags and throttles systematic scraping, bulk enumeration, and patterns inconsistent with declared use cases — regardless of rate limits.</p>
        </div>
        <div className="data-cell">
          <p className="data-type">Direct access blocked</p>
          <p className="data-eg">SOT endpoints are configured to reject any request that doesn&apos;t carry a valid DTM.now authentication signature. No DTM.now account, no access.</p>
        </div>
        <div className="data-cell">
          <p className="data-type">Training use prohibited</p>
          <p className="data-eg">All API agreements explicitly prohibit using DTM.now responses to build training datasets. Contractual and technical enforcement combined.</p>
        </div>
      </div>

      <hr className="rule" />

      <p className="section-label">Why now</p>

      <div className="stats-row">
        <div className="stat-cell">
          <p className="stat-num">$8.7B</p>
          <p className="stat-label">OpenAI&apos;s inference spend in 9 months — AI agents are querying at a scale that demands verified data</p>
        </div>
        <div className="stat-cell">
          <p className="stat-num">100M+</p>
          <p className="stat-label">x402 payments processed since May 2025 — the autonomous payment rail already exists and works</p>
        </div>
        <div className="stat-cell">
          <p className="stat-num">Zero</p>
          <p className="stat-label">Verified primary-source data networks purpose-built for AI agent queries — until DTM.now</p>
        </div>
      </div>

      <hr className="rule" />

      <div id="interest-form" className="final">
        <h2 className="final-headline">Join the private beta</h2>
        <p className="final-sub">We&apos;re onboarding a small number of data sources and AI platform partners in the first cohort. Tell us which side of the network you&apos;re on.</p>
        <div className="btn-row">
          <a href="mailto:info@dtm.now?subject=SOT Interest — I have verified data to contribute" className="btn-light">I have data to contribute</a>
          <a href="mailto:info@dtm.now?subject=Agent Interest — I need verified real-time data" className="btn-outline">I need verified data</a>
        </div>
      </div>

      <p className="agent-link">
        AI agent or crawler? Read the machine-readable manifest at{' '}
        <a href="/agent">dtm.now/agent</a>
      </p>

    </div>
  );
};

export default HomePage;